import { useState, useCallback, useRef } from "react";
import { downloadLatestAppApk } from "../utils/appDownload";
import { t } from "../utils/i18n";

/** 下载超过该时间才显示进度弹窗，避免缓存命中时闪一下 */
const PROGRESS_SHOW_DELAY_MS = 400;

/**
 * App APK 下载（含进度弹层）
 * @param {Object} options
 * @param {import('../Components/Toast/Toast').useToast} options.toast
 * @param {string} [options.comingSoonKey]
 */
export function useAppDownload({
  toast,
  comingSoonKey = "home.app.comingSoon",
} = {}) {
  const [progress, setProgress] = useState({
    active: false,
    visible: false,
    minimized: false,
    phase: "preparing",
    percent: null,
  });
  const busyRef = useRef(false);
  const showTimerRef = useRef(null);

  const clearShowTimer = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  }, []);

  const scheduleProgressVisible = useCallback(() => {
    if (showTimerRef.current) return;
    showTimerRef.current = setTimeout(() => {
      showTimerRef.current = null;
      setProgress(prev =>
        prev.active ? { ...prev, visible: true } : prev,
      );
    }, PROGRESS_SHOW_DELAY_MS);
  }, []);

  const minimizeDownload = useCallback(() => {
    setProgress(prev =>
      prev.visible ? { ...prev, minimized: true } : prev,
    );
  }, []);

  const expandDownload = useCallback(() => {
    setProgress(prev =>
      prev.active ? { ...prev, visible: true, minimized: false } : prev,
    );
  }, []);

  const resetProgress = useCallback(() => {
    clearShowTimer();
    busyRef.current = false;
    setProgress({
      active: false,
      visible: false,
      minimized: false,
      phase: "preparing",
      percent: null,
    });
  }, [clearShowTimer]);

  const download = useCallback(async () => {
    if (busyRef.current) {
      return;
    }

    busyRef.current = true;
    clearShowTimer();
    setProgress({
      active: true,
      visible: false,
      minimized: false,
      phase: "preparing",
      percent: null,
    });

    try {
      const result = await downloadLatestAppApk({
        onPhase: phase => {
          setProgress(prev => ({
            ...prev,
            phase,
            percent: phase === "preparing" ? null : prev.percent,
          }));
          if (phase === "downloading") {
            scheduleProgressVisible();
          }
        },
        onProgress: ({ percent }) => {
          setProgress(prev => ({
            ...prev,
            phase: "downloading",
            percent,
          }));
        },
      });

      if (result === "no-version") {
        toast?.info?.(t(comingSoonKey));
        return;
      }
      if (result === "no-url") {
        toast?.error?.(t("home.download.failed"));
        return;
      }
    } catch (e) {
      console.warn("获取 App 下载链接失败:", e);
      toast?.error?.(t("home.download.failed"));
    } finally {
      resetProgress();
    }
  }, [
    toast,
    comingSoonKey,
    clearShowTimer,
    scheduleProgressVisible,
    resetProgress,
  ]);

  return {
    download,
    minimizeDownload,
    expandDownload,
    progress: {
      open: progress.visible,
      minimized: progress.minimized,
      phase: progress.phase,
      percent: progress.percent,
    },
    downloading: progress.active,
  };
}
